import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addDoc, collection, doc, getDocs, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useCompanySession } from '../session';
import CompanyLayout from '../components/CompanyLayout';
import { publishAttestation, attestationToken, verifyUrl } from '../attestation';
import { openMotorCertificatePrint, printPayloadFromPolicy } from '../pulsePrint';
import { POLICY_CATEGORIES, calculateGrossPremium, findProduct, hashPin, expandGambiaPhone } from '../policyCatalog';
import OutreachButtons from '../components/OutreachButtons';
import { recordPremiumPayment } from '../payments';
import {
    certificateMessage,
    formatMsisdn,
    isWalletMethod,
    useTenantWallets,
    payInstruction,
    walletPayNumber,
    walletBrand
} from '../wallets';
import { createPaymentIntent } from '../payments';
import { PaymentMethodSelect } from '../components/WalletPayBox';
import { defaultPaymentMethod, moduleOn, useTenantFeatures } from '../tenantFeatures';
import { useI18n } from '../i18n';

const emptyForm = {
    clientMode: 'new',
    customerId: '',
    fullName: '',
    nationalId: '',
    phone: '',
    email: '',
    address: '',
    occupation: '',
    dateOfBirth: '',
    gender: '',
    pin: '',
    categoryId: '',
    productId: '',
    vehicleRegistration: '',
    vehicleMake: '',
    vehicleModel: '',
    vehicleYear: '',
    vehicleValue: '',
    vehicleUse: 'private',
    vehicleVin: '',
    ecowasBrownCard: false,
    sumAssured: '',
    termYears: '10',
    beneficiaryName: '',
    beneficiaryRelation: '',
    livesCovered: '1',
    childName: '',
    targetFund: '',
    propertyAddress: '',
    occupancy: 'home',
    sumInsured: '',
    construction: 'concrete',
    itemDescription: '',
    ageBracket: 'adult',
    dependentsCount: '0',
    hasPreExistingConditions: false,
    paymentMethod: 'cash',
    paymentRef: '',
    notes: ''
};

function Field({ label, children }) {
    return (
        <label className="bd-field">
            <span>{label}</span>
            {children}
        </label>
    );
}

function IssuedFollowup({ issued, wallets, session }) {
    const [text, setText] = useState('');
    useEffect(() => {
        let cancelled = false;
        (async () => {
            let url = window.location.origin + '/verify';
            try {
                const token = await attestationToken(session.tenantId, issued.policy.id);
                url = verifyUrl(token);
            } catch (err) { /* plate search still works */ }
            if (cancelled) return;
            setText(certificateMessage({
                companyName: session.companyName,
                name: issued.name,
                plate: issued.policy && issued.policy.vehicle && issued.policy.vehicle.registrationNumber,
                expiry: issued.policy && issued.policy.expiryDate ? new Date(issued.policy.expiryDate).toLocaleDateString('en-GB') : '',
                url,
                pin: issued.pin
            }));
        })();
        return () => { cancelled = true; };
    }, [issued, session, wallets]);
    const payTo = formatMsisdn(walletPayNumber(wallets, issued.paymentMethod));
    return (
        <div className="mt-3">
            {!issued.paid && (
                <p className="small">{payInstruction({ amount: issued.premium, method: issued.paymentMethod, number: payTo, policyNumber: issued.policyNumber, name: issued.name, companyName: session.companyName })}</p>
            )}
            <div className="d-flex justify-content-center">
                <OutreachButtons phone={issued.phone} text={text} />
            </div>
        </div>
    );
}

function NewPolicy() {
    const { session, loading } = useCompanySession();
    const { t } = useI18n();
    const wallets = useTenantWallets(session && session.tenantId);
    const features = useTenantFeatures(session && session.tenantId);
    const [customers, setCustomers] = useState([]);
    const [form, setForm] = useState(emptyForm);
    const [premium, setPremium] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [issued, setIssued] = useState(null);
    const navigate = useNavigate();

    const set = (patch) => {
        setForm((prev) => ({ ...prev, ...patch }));
        setPremium(null);
    };

    useEffect(() => {
        if (!session?.tenantId) return;
        getDocs(collection(db, 'tenants', session.tenantId, 'customers')).then((snap) => {
            const list = [];
            snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
            list.sort((a, b) => String(a.fullName || '').localeCompare(String(b.fullName || '')));
            setCustomers(list);
        });
    }, [session]);

    const selectedCustomer = customers.find((c) => c.id === form.customerId);
    const { category, product } = findProduct(form.categoryId, form.productId);

    const pickCustomer = (id) => {
        if (!id) {
            set({ clientMode: 'new', customerId: '' });
            return;
        }
        const row = customers.find((c) => c.id === id);
        set({
            clientMode: 'existing',
            customerId: id,
            fullName: row.fullName || '',
            nationalId: row.nationalId || '',
            phone: row.phone || '',
            email: row.email || '',
            address: row.address || '',
            occupation: row.occupation || '',
            pin: ''
        });
    };

    const suggestPin = () => {
        const digits = expandGambiaPhone(form.phone).slice(-4);
        set({ pin: digits.length === 4 ? digits : String(1000 + Math.floor(Math.random() * 9000)) });
    };

    const runCalculation = () => {
        if (!form.categoryId || !form.productId) {
            setError('Choose a policy type first.');
            return;
        }
        setError('');
        setPremium(calculateGrossPremium(form));
    };

    const rating = useMemo(() => premium || calculateGrossPremium(form), [premium, form]);

    const handleIssue = async (e) => {
        e.preventDefault();
        if (!form.categoryId || !form.productId) return setError('Choose the line of business and a product.');
        if (!form.fullName.trim() || !form.phone.trim() || !form.nationalId.trim()) {
            return setError('Client name, national ID, and phone are required.');
        }
        const quote = calculateGrossPremium(form);
        if (quote.grossPremium <= 0) return setError('Calculate a premium before issuing.');
        setSaving(true);
        setError('');
        try {
            const tId = session.tenantId;
            const phoneNormalized = expandGambiaPhone(form.phone);
            const pin = form.pin.trim() || phoneNormalized.slice(-4);
            let customerId = form.customerId;
            const holder = {
                fullName: form.fullName.trim(),
                nationalId: form.nationalId.trim(),
                phone: form.phone.trim(),
                email: form.email.trim(),
                address: form.address.trim(),
                occupation: form.occupation.trim()
            };

            if (!customerId) {
                const customerRef = await addDoc(collection(db, 'tenants', tId, 'customers'), {
                    ...holder,
                    phoneNormalized,
                    dateOfBirth: form.dateOfBirth || null,
                    gender: form.gender || null,
                    pinIssued: true,
                    lookupPin: pin,
                    createdAt: new Date().toISOString(),
                    brokerId: session.role === 'broker' ? session.uid : null,
                    brokerEmail: session.role === 'broker' ? session.email : null
                });
                customerId = customerRef.id;
            }

            if (pin && phoneNormalized) {
                await setDoc(doc(db, 'lookupIndex', tId + '_' + phoneNormalized), {
                    phoneNormalized,
                    pinHash: await hashPin(phoneNormalized, pin),
                    tenantId: tId,
                    customerId,
                    companyName: session.companyName
                });
            }

            const now = new Date();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const yearShort = String(now.getFullYear()).slice(-2);
            const seq = String(now.getTime()).slice(-6);
            const prefix = form.categoryId === 'motor' ? 'MTR' : form.categoryId === 'medical' ? 'MED' : form.categoryId === 'life' ? 'LIF' : 'PRP';
            const policyNumber = prefix + product.code + '/' + seq + '/' + month + yearShort;
            const needsApproval = session.role === 'broker' || session.role === 'underwriter';
            const walletPaid = isWalletMethod(form.paymentMethod) && String(form.paymentRef || '').trim();
            const paidNow = (form.paymentMethod === 'cash' || walletPaid) && !needsApproval;
            const expiry = new Date(now);
            expiry.setFullYear(expiry.getFullYear() + 1);

            const policyRef = await addDoc(collection(db, 'tenants', tId, 'policies'), {
                customerId,
                policyNumber,
                policyType: category.id.toUpperCase(),
                coverageType: product.id.toUpperCase(),
                productName: product.name,
                productCode: product.code,
                categoryName: category.name,
                premiumAmountGMD: quote.grossPremium,
                premium: { ...quote, formula: 'GP = NRP + WMF + GRA Taxes' },
                paymentMethod: form.paymentMethod.toUpperCase(),
                paymentStatus: paidNow ? 'paid' : 'unpaid',
                status: paidNow ? 'ACTIVE' : (needsApproval ? 'pending' : 'QUOTE'),
                tenantId: tId,
                companyName: session.companyName,
                policyHolder: holder,
                vehicle: form.categoryId === 'motor' ? {
                    registrationNumber: form.vehicleRegistration,
                    vin: form.vehicleVin,
                    make: form.vehicleMake,
                    model: form.vehicleModel,
                    year: form.vehicleYear,
                    declaredValue: Number(form.vehicleValue) || 0,
                    use: form.vehicleUse
                } : null,
                life: form.categoryId === 'life' ? {
                    sumAssured: Number(form.sumAssured) || 0,
                    termYears: Number(form.termYears) || 0,
                    beneficiaryName: form.beneficiaryName,
                    beneficiaryRelation: form.beneficiaryRelation,
                    livesCovered: Number(form.livesCovered) || 1,
                    childName: form.childName,
                    targetFund: Number(form.targetFund) || 0
                } : null,
                property: form.categoryId === 'property' ? {
                    address: form.propertyAddress,
                    occupancy: form.occupancy,
                    sumInsured: Number(form.sumInsured) || 0,
                    construction: form.construction,
                    itemDescription: form.itemDescription
                } : null,
                medical: form.categoryId === 'medical' ? {
                    ageBracket: form.ageBracket,
                    dependentsCount: Number(form.dependentsCount) || 0,
                    hasPreExistingConditions: !!form.hasPreExistingConditions
                } : null,
                ecowasBrownCard: !!form.ecowasBrownCard,
                notes: form.notes,
                requiresApproval: needsApproval,
                effectiveDate: now.toISOString(),
                expiryDate: expiry.toISOString(),
                createdAt: now.toISOString(),
                createdBy: session.uid,
                createdByRole: session.role,
                brokerId: session.role === 'broker' ? session.uid : (selectedCustomer && selectedCustomer.brokerId) || null,
                brokerEmail: session.role === 'broker' ? session.email : (selectedCustomer && selectedCustomer.brokerEmail) || null
            });

            const policySnap = { id: policyRef.id, policyNumber, status: paidNow ? 'ACTIVE' : (needsApproval ? 'pending' : 'QUOTE'), productName: product.name, coverageType: product.id, policyType: category.id, effectiveDate: now.toISOString(), expiryDate: expiry.toISOString(), createdAt: now.toISOString(), vehicle: form.categoryId === 'motor' ? { registrationNumber: form.vehicleRegistration, vin: form.vehicleVin, make: form.vehicleMake, model: form.vehicleModel, year: form.vehicleYear, use: form.vehicleUse } : null, ecowasBrownCard: !!form.ecowasBrownCard, policyHolder: holder, brokerId: session.role === 'broker' ? session.uid : (selectedCustomer && selectedCustomer.brokerId) || null, brokerEmail: session.role === 'broker' ? session.email : (selectedCustomer && selectedCustomer.brokerEmail) || null, premiumAmountGMD: quote.grossPremium };

            if (paidNow) {
                await recordPremiumPayment({
                    tenantId: tId,
                    session,
                    policy: policySnap,
                    customer: holder,
                    wallets,
                    method: form.paymentMethod,
                    reference: form.paymentRef,
                    activate: false
                });
            } else {
                try {
                    await createPaymentIntent({
                        tenantId: tId,
                        policy: policySnap,
                        method: form.paymentMethod,
                        amountGMD: quote.grossPremium,
                        wallets,
                        status: 'PENDING'
                    });
                } catch (err) {
                    console.error(err);
                }
                try {
                    await publishAttestation({
                        tenantId: tId,
                        companyName: session.companyName,
                        customer: holder,
                        policy: policySnap
                    });
                } catch (err) {
                    console.error(err);
                }
            }

            setIssued({ policyNumber, pin, phone: form.phone, name: form.fullName, premium: quote.grossPremium, status: policySnap.status, policy: policySnap, customer: holder, paymentMethod: form.paymentMethod, paymentRef: form.paymentRef, paid: paidNow });
        } catch (err) {
            setError(err.message || 'Could not save this policy.');
        }
        setSaving(false);
    };

    if (loading || !session) return <div className="p-5">{t('common.loading')}</div>;

    if (issued) {
        return (
            <CompanyLayout session={session}>
                <div className="bd-card" style={{ textAlign: 'center', padding: 40 }}>
                    <div style={{ fontSize: 40, marginBottom: 8 }}>✅</div>
                    <h3>{t('policy.captured')}</h3>
                    <p className="mb-1"><strong>{issued.name}</strong></p>
                    <p className="font-monospace mb-1">{issued.policyNumber}</p>
                    <p>Premium D{issued.premium.toLocaleString()}.00 · {issued.status}</p>
                    {moduleOn(features, 'clientPortal') && (
                        <p className="text-muted">{t('policy.clientPortalLine', { phone: issued.phone, pin: issued.pin })}</p>
                    )}
                    <IssuedFollowup issued={issued} wallets={wallets} session={session} />
                    <div className="d-flex gap-2 justify-content-center flex-wrap mt-3">
                        {issued.status === 'ACTIVE' && issued.policy && issued.policy.vehicle && (
                            <button
                                type="button"
                                className="bd-btn bd-btn-success"
                                onClick={() => openMotorCertificatePrint({
                                    certificates: [printPayloadFromPolicy(issued.policy, issued.customer, {
                                        managerName: session.displayName,
                                        examinedBy: session.displayName,
                                        issuedAt: new Date().toISOString()
                                    })]
                                })}
                            >
                                {t('policy.printCert')}
                            </button>
                        )}
                        <button type="button" className="bd-btn bd-btn-success" onClick={() => { setIssued(null); setForm(emptyForm); setPremium(null); }}>{t('policy.newClientFile')}</button>
                        <button type="button" className="bd-btn bd-btn-secondary" onClick={() => navigate('/dashboard')}>{t('policy.backDashboard')}</button>
                    </div>
                </div>
            </CompanyLayout>
        );
    }

    return (
        <CompanyLayout session={session}>
            <form onSubmit={handleIssue}>
                {error && <div className="alert alert-danger">{error}</div>}

                <div className="bd-card">
                    <h3>{t('policy.headingClient')}</h3>
                    <div className="bd-search" style={{ marginBottom: 16 }}>
                        <select value={form.customerId} onChange={(e) => pickCustomer(e.target.value)}>
                            <option value="">{t('policy.newClient')}</option>
                            {customers.map((c) => (
                                <option key={c.id} value={c.id}>{c.fullName} · {c.phone}</option>
                            ))}
                        </select>
                    </div>
                    <div className="bd-form-grid">
                        <Field label={t('policy.fullName')}>
                            <input className="form-control" value={form.fullName} onChange={(e) => set({ fullName: e.target.value })} required />
                        </Field>
                        <Field label={t('policy.nationalId')}>
                            <input className="form-control" value={form.nationalId} onChange={(e) => set({ nationalId: e.target.value })} required />
                        </Field>
                        <Field label={t('policy.mobile')}>
                            <input className="form-control" value={form.phone} onChange={(e) => set({ phone: e.target.value })} placeholder="+220 700001001" required />
                        </Field>
                        <Field label={t('policy.email')}>
                            <input type="email" className="form-control" value={form.email} onChange={(e) => set({ email: e.target.value })} />
                        </Field>
                        <Field label={t('policy.address')}>
                            <input className="form-control" value={form.address} onChange={(e) => set({ address: e.target.value })} />
                        </Field>
                        <Field label={t('policy.occupation')}>
                            <input className="form-control" value={form.occupation} onChange={(e) => set({ occupation: e.target.value })} />
                        </Field>
                        <Field label={t('policy.dob')}>
                            <input type="date" className="form-control" value={form.dateOfBirth} onChange={(e) => set({ dateOfBirth: e.target.value })} />
                        </Field>
                        <Field label={t('policy.gender')}>
                            <select className="form-select" value={form.gender} onChange={(e) => set({ gender: e.target.value })}>
                                <option value="">{t('policy.select')}</option>
                                <option value="female">{t('policy.female')}</option>
                                <option value="male">{t('policy.male')}</option>
                                <option value="other">{t('policy.other')}</option>
                            </select>
                        </Field>
                        <Field label={t('policy.pin')}>
                            <div className="d-flex gap-2">
                                <input className="form-control" inputMode="numeric" maxLength="4" value={form.pin} onChange={(e) => set({ pin: e.target.value.replace(/\D/g, '').slice(0, 4) })} placeholder="e.g. 1001" />
                                <button type="button" className="bd-btn bd-btn-secondary bd-btn-sm" onClick={suggestPin}>{t('policy.suggest')}</button>
                            </div>
                        </Field>
                    </div>
                </div>

                <div className="bd-card">
                    <h3>{t('policy.headingChoose')}</h3>
                    <p className="text-muted small mb-3">{t('policy.chooseHint')}</p>
                    <div className="bd-product-grid">
                        {POLICY_CATEGORIES.map((cat) => (
                            <button
                                type="button"
                                key={cat.id}
                                className={'bd-product-card' + (form.categoryId === cat.id ? ' selected' : '')}
                                onClick={() => set({ categoryId: cat.id, productId: '' })}
                            >
                                <strong>{cat.name}</strong>
                                <span>{cat.blurb}</span>
                            </button>
                        ))}
                    </div>
                    {category && (
                        <div className="mt-3">
                            <h6 className="mb-2">{category.name}</h6>
                            <div className="bd-product-grid">
                                {category.products.map((item) => (
                                    <button
                                        type="button"
                                        key={item.id}
                                        className={'bd-product-card' + (form.productId === item.id ? ' selected' : '')}
                                        onClick={() => set({ productId: item.id })}
                                    >
                                        <strong>{item.name}</strong>
                                        <span>{item.description}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {product && form.categoryId === 'motor' && (
                    <div className="bd-card">
                        <h3>{t('policy.headingVehicle')}</h3>
                        <div className="bd-form-grid">
                            <Field label={t('policy.regNumber')}><input className="form-control" value={form.vehicleRegistration} onChange={(e) => set({ vehicleRegistration: e.target.value })} required /></Field>
                            <Field label={t('policy.make')}><input className="form-control" value={form.vehicleMake} onChange={(e) => set({ vehicleMake: e.target.value })} required /></Field>
                            <Field label={t('policy.model')}><input className="form-control" value={form.vehicleModel} onChange={(e) => set({ vehicleModel: e.target.value })} required /></Field>
                            <Field label={t('policy.year')}><input type="number" className="form-control" value={form.vehicleYear} onChange={(e) => set({ vehicleYear: e.target.value })} /></Field>
                            <Field label={t('policy.vin')}><input className="form-control" value={form.vehicleVin} onChange={(e) => set({ vehicleVin: e.target.value.toUpperCase() })} placeholder="e.g. JTDKN3DU0A0123456" /></Field>
                            <Field label={t('policy.declaredValue')}><input type="number" className="form-control" value={form.vehicleValue} onChange={(e) => set({ vehicleValue: e.target.value })} required /></Field>
                            <Field label={t('policy.use')}>
                                <select className="form-select" value={form.vehicleUse} onChange={(e) => set({ vehicleUse: e.target.value })}>
                                    <option value="private">{t('policy.private')}</option>
                                    <option value="taxi">{t('policy.taxi')}</option>
                                    <option value="goods">{t('policy.goods')}</option>
                                </select>
                            </Field>
                        </div>
                        {moduleOn(features, 'ecowas') && (
                            <label className="d-flex align-items-center gap-2 mt-3">
                                <input type="checkbox" checked={form.ecowasBrownCard} onChange={(e) => set({ ecowasBrownCard: e.target.checked })} />
                                {t('policy.ecowas')}
                            </label>
                        )}
                    </div>
                )}

                {product && form.categoryId === 'life' && (
                    <div className="bd-card">
                        <h3>{t('policy.headingLife')}</h3>
                        <div className="bd-form-grid">
                            <Field label="Sum assured (GMD)"><input type="number" className="form-control" value={form.sumAssured} onChange={(e) => set({ sumAssured: e.target.value })} required /></Field>
                            {(form.productId === 'term_life' || form.productId === 'endowment' || form.productId === 'education') && (
                                <Field label="Term (years)"><input type="number" className="form-control" value={form.termYears} onChange={(e) => set({ termYears: e.target.value })} /></Field>
                            )}
                            {form.productId === 'group_life' && (
                                <Field label="Lives covered"><input type="number" className="form-control" value={form.livesCovered} onChange={(e) => set({ livesCovered: e.target.value })} /></Field>
                            )}
                            {form.productId === 'education' && (
                                <>
                                    <Field label="Child's name"><input className="form-control" value={form.childName} onChange={(e) => set({ childName: e.target.value })} /></Field>
                                    <Field label="Target education fund (GMD)"><input type="number" className="form-control" value={form.targetFund} onChange={(e) => set({ targetFund: e.target.value })} /></Field>
                                </>
                            )}
                            <Field label="Beneficiary"><input className="form-control" value={form.beneficiaryName} onChange={(e) => set({ beneficiaryName: e.target.value })} /></Field>
                            <Field label="Relationship"><input className="form-control" value={form.beneficiaryRelation} onChange={(e) => set({ beneficiaryRelation: e.target.value })} /></Field>
                        </div>
                    </div>
                )}

                {product && form.categoryId === 'property' && (
                    <div className="bd-card">
                        <h3>{t('policy.headingProperty')}</h3>
                        <div className="bd-form-grid">
                            <Field label="Property / risk address"><input className="form-control" value={form.propertyAddress} onChange={(e) => set({ propertyAddress: e.target.value })} required /></Field>
                            <Field label="Occupancy">
                                <select className="form-select" value={form.occupancy} onChange={(e) => set({ occupancy: e.target.value })}>
                                    <option value="home">Home</option>
                                    <option value="shop">Shop</option>
                                    <option value="office">Office</option>
                                    <option value="warehouse">Warehouse</option>
                                </select>
                            </Field>
                            <Field label="Sum insured (GMD)"><input type="number" className="form-control" value={form.sumInsured} onChange={(e) => set({ sumInsured: e.target.value })} required /></Field>
                            <Field label="Construction">
                                <select className="form-select" value={form.construction} onChange={(e) => set({ construction: e.target.value })}>
                                    <option value="concrete">Concrete</option>
                                    <option value="block">Block</option>
                                    <option value="mixed">Mixed</option>
                                </select>
                            </Field>
                            {form.productId === 'all_risks' && (
                                <Field label="Items to cover"><input className="form-control" value={form.itemDescription} onChange={(e) => set({ itemDescription: e.target.value })} placeholder="Laptops, jewellery, equipment" /></Field>
                            )}
                        </div>
                    </div>
                )}

                {product && form.categoryId === 'medical' && (
                    <div className="bd-card">
                        <h3>{t('policy.headingMedical')}</h3>
                        <div className="bd-form-grid">
                            <Field label="Age bracket">
                                <select className="form-select" value={form.ageBracket} onChange={(e) => set({ ageBracket: e.target.value })}>
                                    <option value="child">Child</option>
                                    <option value="adult">Adult</option>
                                    <option value="senior">Senior</option>
                                </select>
                            </Field>
                            {form.productId === 'private_medical' && (
                                <Field label="Dependents"><input type="number" className="form-control" value={form.dependentsCount} onChange={(e) => set({ dependentsCount: e.target.value })} /></Field>
                            )}
                        </div>
                        {form.productId === 'private_medical' && (
                            <label className="d-flex align-items-center gap-2 mt-3">
                                <input type="checkbox" checked={form.hasPreExistingConditions} onChange={(e) => set({ hasPreExistingConditions: e.target.checked })} />
                                Pre-existing conditions disclosed
                            </label>
                        )}
                    </div>
                )}

                {product && (
                    <div className="bd-card">
                        <h3>{t('policy.headingPremium')}</h3>
                        <p className="small text-muted">{product.name} · GP = NRP + Wakala fee + GRA tax</p>
                        <div className="bd-form-grid">
                            <Field label={t('policy.paymentMethod')}>
                                <PaymentMethodSelect
                                    wallets={wallets}
                                    features={features}
                                    value={form.paymentMethod || defaultPaymentMethod()}
                                    onChange={(paymentMethod) => set({ paymentMethod })}
                                />
                            </Field>
                            {isWalletMethod(form.paymentMethod) && (
                                <Field label={walletBrand(form.paymentMethod) + ' confirmation'}>
                                    <input className="form-control" value={form.paymentRef} onChange={(e) => set({ paymentRef: e.target.value })} placeholder="Txn id or sender name" />
                                </Field>
                            )}
                            <Field label={t('policy.notes')}>
                                <input className="form-control" value={form.notes} onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))} />
                            </Field>
                        </div>
                        {premium && (
                            <div className="bd-live-stats mt-3">
                                <div className="bd-stat-card"><div className="bd-label">{t('policy.nrp')}</div><div className="bd-value">D {premium.nrp.toLocaleString()}</div></div>
                                <div className="bd-stat-card"><div className="bd-label">{t('policy.wmf')}</div><div className="bd-value">D {premium.wmf.toLocaleString()}</div></div>
                                <div className="bd-stat-card"><div className="bd-label">{t('policy.gra')}</div><div className="bd-value">D {premium.graTax.toLocaleString()}</div></div>
                                <div className="bd-stat-card"><div className="bd-label">{t('policy.gross')}</div><div className="bd-value green">D {premium.grossPremium.toLocaleString()}</div></div>
                            </div>
                        )}
                        {isWalletMethod(form.paymentMethod) && (
                            <p className="small text-muted mt-2 mb-0">
                                {payInstruction({
                                    amount: (premium && premium.grossPremium) || 0,
                                    method: form.paymentMethod,
                                    number: formatMsisdn(walletPayNumber(wallets, form.paymentMethod)),
                                    policyNumber: '',
                                    name: form.fullName
                                })}
                            </p>
                        )}
                        <div className="d-flex gap-2 flex-wrap mt-3">
                            <button type="button" className="bd-btn bd-btn-secondary" onClick={runCalculation}>{t('policy.calculate')}</button>
                            <button type="submit" className="bd-btn bd-btn-success" disabled={saving || rating.grossPremium <= 0}>
                                {saving ? t('policy.saving') : t('policy.issue')}
                            </button>
                        </div>
                    </div>
                )}
            </form>
        </CompanyLayout>
    );
}

export default NewPolicy;
